import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { DownloadManagerItemState, DownloadMode, DownloadAllStatus } from '../reducer';
import NativeApi from '../../../utils/nativeApi';
import UserAgentUtils from '../../../utils/userAgent';
import DriveItemUtils from '../../../utils/driveItem';
import { downloadsFolder, tempFolder } from '../../../utils';
import { Api } from '../../../typings/api';
import { createNotification } from '../../NotificationBar/actions';
import { getOrFetchDriveItemMeta } from '../../DriveItemList/actions';
import DownloadManagerUtils from '../../../utils/downloadManager';
import { updateDownload, removeDownloadItem, removeDependentFromDependencies, setIsDeletingAllStatus, setDownloadAllStatus } from './store';
const Q = require('q');

const emptyDownloadCache = () => (dispatch: Dispatch<StoreState>): Q.Promise<{} | void> => {
	return NativeApi.existDirectory(tempFolder)
		.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${tempFolder}`) : {});
};

const deleteItem = (driveItem: Api.DriveItem) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const { downloadManager } = getState();
		const driveItemInDownloadManager = downloadManager.items[driveItem.id];

		if (driveItemInDownloadManager) {
			const { dependencies, dependents } = driveItemInDownloadManager;
			if (DownloadManagerUtils.isDependencyOfPersistentDriveItem(downloadManager.items, driveItem.id, [driveItem.id])) {
				if (driveItemInDownloadManager.mode === DownloadMode.Requested) {
					dispatch(updateDownload(driveItem, DownloadMode.Dependency, dependencies, dependents));
				}
				return Q.Promise.resolve();
			} else {
				return dispatch(nativeDelete(driveItem))
					.then(() => dispatch(removeDependentFromDependencies(driveItem)))
					.then(() => dispatch(removeDownloadItem(driveItem.id)))
					.then(() => dispatch(deleteItems(dependencies)))
					.catch(error => dispatch(createNotification('error.delete_fail', { id: driveItem.id })));
			}
		}
		return dispatch(nativeDelete(driveItem));
	};

const deleteItems = (ids: number[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const { driveList, downloadManager } = getState();
	const { driveItems } = driveList;
	const { items } = downloadManager;

	let promise: Q.Promise<{} | void> = Q.Promise.resolve();

	ids.forEach(dependencyId => {
		if (items[dependencyId]) {
			promise = promise.then(() => dispatch(deleteItem(driveItems[dependencyId])));
		}
	});

	return promise;
};

const nativeDelete = (driveItem: Api.DriveItem) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		return NativeApi.existFile(`${downloadsFolder}/${driveItem.id}.zip`)
			.then(fileExist => fileExist ? NativeApi.removeFile(`${downloadsFolder}/${driveItem.id}.zip`) : {})
			.then(() => {
				switch (driveItem.type) {
					case Api.DriveItemType.Directory:
					case Api.DriveItemType.Publication:
					case Api.DriveItemType.Component:
					case Api.DriveItemType.Template:
					case Api.DriveItemType.Gallery:
						return NativeApi.existDirectory(`${downloadsFolder}/${driveItem.id}`)
							.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${downloadsFolder}/${driveItem.id}`) : {});
					default:
						const fileName = DriveItemUtils.getDownloadedFileName(driveItem);
						return NativeApi.existFile(`${downloadsFolder}/${fileName}`)
							.then(fileExist => fileExist ? NativeApi.removeFile(`${downloadsFolder}/${fileName}`) : {});
				}
			});
	};

const deletePendingDownloads = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{} | void> => {
	const { downloadManager } = getState();
	const { items } = downloadManager;
	let promises: Array<Promise<{} | void>> = [];

	Object.keys(items).forEach(key => {
		const item: DownloadManagerItemState = items[key];
		const id = parseInt(key, 10);

		if ((item.mode === DownloadMode.Required || item.mode === DownloadMode.Requested) && !DownloadManagerUtils.isItemDownloaded(downloadManager, id)) {
			promises.push(
				dispatch(getOrFetchDriveItemMeta(id, false)).then((driveItem: Api.DriveItem) => dispatch(deleteItem(driveItem)))
			);
		}
	});
	return Q.Promise.all(promises);
};

const removeAllDownloads = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	dispatch(setIsDeletingAllStatus(true));
	if (UserAgentUtils.isAndroidApp()) {
		NativeApi.existDirectory(`${tempFolder}`)
			.then(directoryExist => directoryExist ? NativeApi.deleteDirJs(`../../${tempFolder}`) : {})
			.catch(error => dispatch(createNotification('error.delete_fail')));
	}

	dispatch(deleteAllNonRequiredItems())
		.finally(() => dispatch(setIsDeletingAllStatus(false)))
		.then(() => dispatch(setDownloadAllStatus(DownloadAllStatus.NotStarted)))
		.then(() => dispatch(createNotification('info.delete_all')))
		.catch(error => dispatch(createNotification('error.delete_fail')));
};

const deleteAllNonRequiredItems = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{}> => {
	const { downloadManager, driveList } = getState();
	const { items } = downloadManager;
	const { driveItems } = driveList;

	let promises: Q.Promise<{} | void>[] = [];

	Object.keys(items).forEach(id => {
		if (items[id].mode !== DownloadMode.Required) {
			promises.push(dispatch(deleteItem(driveItems[id])));
		}
	});

	return Q.allSettled(promises);
};

export { emptyDownloadCache, deletePendingDownloads, deleteItem, nativeDelete, removeAllDownloads };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/delete.ts